// src/components/settings/InDesignExportSettings.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { MinistryPerson } from '../../pages/StaffPage';
import {
  EightAmTemplatePanel, EightAmChoices, emptyEightAmChoices,
  BaptismCandidate, encodeBaptismPeople, decodeBaptismPeople,
} from './EightAmTemplatePanel';
import apiClient from '../../utils/apiClient';
import './InDesignExportSettings.css';
import { createIndesignExports, downloadExport, ExportPreferences, BulletinKey } from './indesignExportBundle';
import { ReadingsConfigPanel, LectionaryEntry, ReadingSelections } from './ReadingsConfigPanel';
import { AddedPartsPanel } from './AddedPartsPanel';

interface UpcomingService {
  service_id: number;
  date: string;
  name: string;
  time: string;
  ot_reading: string | null;
  nt_reading: string | null;
  gospel_reading: string | null;
  psalm_reading: string | null;
  special_service_name: string | null;
  season: string;
  liturgical_year: string;
  church_name: string;
  church_id: number;
}

// 'spanishFull' = THE 12:30 (update-bulletin-spanish.jsx, rebuilt fully each week
// since 2026-09-22). It takes the 12:30 column of the seasonal plan.
// 'spanish' = the plain Spanish export, now only for Spanish Morning Prayer
// (update-bulletin-mp-spanish.jsx).
type ServiceType = '8am' | '10am' | 'spanish' | 'spanishFull' | 'bilingual';
/** Bulletins whose prayers come from the seasonal plan (the 10 am since 2026-10-23). */
const usesPlan = (v: ServiceType) => v === '8am' || v === '10am' || v === 'spanishFull';

/** Response from the lectionary API */
interface LectionaryResponse {
  entryKey: string;
  year: string;
  entry: LectionaryEntry;
  collectCount?: number;
  prefaceKey?: string | null;
  /** Track the service's stored readings are on; null when undetermined. */
  defaultTrack?: '1' | '2' | null;
}

interface PrefaceOption {
  id: string;
  label: string;
  matchKeys: string[];
  text: string;
}

type EucharistChoice = '' | 'A' | 'B' | 'C' | 'D' | 'EOW2';
type PopChoice = '' | 'form-i' | 'form-i-short' | 'form-ii' | 'form-iii' | 'form-iv' | 'form-v' | 'form-vi';
type PostcommunionChoice = '' | 'eow1' | 'eow2' | 'bcp-almighty';

export const InDesignExportSettings: React.FC = () => {
  const [services, setServices] = useState<UpcomingService[]>([]);
  const [selectedService, setSelectedService] = useState<UpcomingService | null>(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState<ServiceType>('8am');
  const [eightAmReady, setEightAmReady] = useState(false);
  const [exporting, setExporting] = useState<ServiceType | BulletinKey | 'all' | 'saving' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedPreferences, setSavedPreferences] = useState<ExportPreferences | null>(null);
  const [eightAmPending, setEightAmPending] = useState(false);
  const [notice, setNotice] = useState('');
  const [preacher, setPreacher] = useState<string>('');
  const [preacherSp, setPreacherSp] = useState<string>('');

  const [preacherId, setPreacherId] = useState<number | null>(null);
  const [preachers, setPreachers] = useState<MinistryPerson[]>([]);
  useEffect(() => {
    if (!savedPreferences?.preacher_managed) return;
    let cancelled = false;
    apiClient.get('/staff/people').then(res => { if (!cancelled) setPreachers(res.data.people); })
      .catch(() => { if (!cancelled) setError('Could not load preachers. Reload this page to try again.'); });
    return () => { cancelled = true; };
  }, [savedPreferences?.preacher_managed]);

  // Lectionary state
  const [lectionaryEntry, setLectionaryEntry] = useState<LectionaryEntry | null>(null);
  const [lectionaryLoading, setLectionaryLoading] = useState(false);
  const [lectionaryError, setLectionaryError] = useState<string | null>(null);
  const [readingSelections, setReadingSelections] = useState<ReadingSelections>({
    track: '2',
    options: {},
  });
  const [collectCount, setCollectCount] = useState<number>(1);
  const [collectIndex, setCollectIndex] = useState<number>(0);

  // Liturgy selections (Eucharistic Prayer + Prayers of the People + Proper Preface)
  const [eucharist, setEucharist] = useState<EucharistChoice>('');
  const [eucharistForm, setEucharistForm] = useState<'short' | 'long'>('short');
  const [pop, setPop] = useState<PopChoice>('');
  // Holy Baptism (replaces Prayers of the People when chosen). Any number of
  // candidates: with two or more, the rite switches to the prayer books' plural
  // wording and the three naming moments repeat once per person.
  const [baptismForm, setBaptismForm] = useState<'' | 'older' | 'younger' | 'group'>('');
  const [baptismPeople, setBaptismPeople] = useState<BaptismCandidate[]>([{ name: '', gender: 'f' }]);
  // Renewal of Baptismal Vows (shares the baptism/POP slot)
  const [renewal, setRenewal] = useState<boolean>(false);
  // Added parts for this service (First Communion, a blessing): "id@place|id@place".
  const [parts, setParts] = useState<string>('');
  const [postcommunion, setPostcommunion] = useState<PostcommunionChoice>('');
  const [fraction, setFraction] = useState<string>('');
  const [fractionOptions, setFractionOptions] = useState<{ id: string; label: string }[]>([]);
  const [preface, setPreface] = useState<string>('');
  const [prefaceOptions, setPrefaceOptions] = useState<PrefaceOption[]>([]);
  const [prefaceKeyFromCollect, setPrefaceKeyFromCollect] = useState<string | null>(null);

  const applyChoices = useCallback((choices: EightAmChoices) => {
    setEucharist(choices.eucharist); setEucharistForm(choices.form); setPop(choices.pop);
    setPostcommunion(choices.postcommunion); setPreface(choices.preface);
    setFraction(choices.fraction || '');
    setBaptismForm(choices.baptism);
    // Settings saved before multi-candidate support carry only the single
    // baptismName/baptismGender pair; fall back to those when the list is absent.
    setBaptismPeople(
      decodeBaptismPeople(choices.baptismNames || '', choices.baptismName, choices.baptismGender)
    );
    setRenewal(choices.renewal === 'on');
    setParts(choices.parts || '');
  }, []);
  const currentChoices: EightAmChoices = {
    eucharist, form: eucharistForm, pop, postcommunion, preface, fraction,
    baptism: baptismForm,
    baptismName: baptismPeople[0]?.name || '',
    baptismGender: baptismPeople[0]?.gender || 'f',
    baptismNames: encodeBaptismPeople(baptismPeople),
    renewal: renewal ? 'on' : '',
    parts,
  };
  // Prayer choices belong to one service/variant. Never carry 8am picks into Spanish.
  useEffect(() => {
    applyChoices({ ...emptyEightAmChoices, preface: usesPlan(variant) ? 'auto' : '' });
    setEightAmReady(false);
    setEightAmPending(false);
  }, [selectedService?.service_id, variant, applyChoices]);

  // Fetch upcoming services. Fetch both St Luke's (church_id=2, English 8am/10am)
  // and San Lucas (church_id=3, Spanish 12:30 PM) — same physical building, modeled
  // as separate church records. We need San Lucas in the list so the Spanish export
  // can find the 12:30 PM service_id even when the user has the 10am card selected.
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const [stLukes, sanLucas] = await Promise.all([
          apiClient.get<UpcomingService[]>('/bulletins/upcoming-services', {
            params: { limit: 12, churchId: 2 }
          }),
          apiClient.get<UpcomingService[]>('/bulletins/upcoming-services', {
            params: { limit: 12, churchId: 3 }
          })
        ]);
        const merged = [...stLukes.data.map(s => ({ ...s, church_id: 2 })), ...sanLucas.data.map(s => ({ ...s, church_id: 3 }))].sort((a, b) =>
          a.date === b.date ? a.time.localeCompare(b.time) : a.date.localeCompare(b.date)
        );
        setServices(merged);
        setSelectedService(merged.find(s => s.church_id === 2 && s.time.slice(0, 5) === '10:00') || merged.find(s => s.church_id === 2) || null);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch services:', err);
        setError('Failed to load upcoming services');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Spoken fraction anthems offered in Spanish (the 12:30 full rebuild).
  useEffect(() => {
    apiClient.get<{ id: string; label: string }[]>('/bulletins/indesign-settings/fraction-anthems', { params: { lang: 'es' } })
      .then((res) => setFractionOptions(res.data || []))
      .catch((err) => console.warn('[InDesignExport] Could not load fraction anthems:', err));
  }, []);

  // Fetch the list of Proper Preface options once on mount.
  useEffect(() => {
    apiClient.get<PrefaceOption[]>('/bulletins/prefaces')
      .then((res) => setPrefaceOptions(res.data || []))
      .catch((err) => console.warn('[InDesignExport] Could not load prefaces:', err));
  }, []);

  // Auto-default the preface dropdown based on the BCP "Preface of X" name
  // attached to the day's collect. User can still override after this fires.
  useEffect(() => {
    if (usesPlan(variant) || !prefaceKeyFromCollect || prefaceOptions.length === 0) return;
    if (eucharist === 'D' || eucharist === 'EOW2') return;
    const match = prefaceOptions.find((opt) =>
      opt.matchKeys.some((k) => k.toLowerCase() === prefaceKeyFromCollect.toLowerCase())
    );
    setPreface(match?.id ?? '');
  }, [prefaceKeyFromCollect, prefaceOptions, eucharist, variant]);

  // EOW2 is long-only and has no Proper Preface — force both when it's picked.
  useEffect(() => {
    if (eucharist === 'EOW2') {
      setEucharistForm('long');
      setPreface('');
    }
  }, [eucharist]);

  // Fetch lectionary data when a service is selected
  useEffect(() => {
    if (!selectedService) {
      setLectionaryEntry(null);
      setLectionaryError(null);
      return;
    }

    let cancelled = false;
    setSavedPreferences(null);
    setNotice('');
    const fetchLectionary = async () => {
      setLectionaryLoading(true);
      setLectionaryError(null);

      try {
        const [response, saved] = await Promise.all([
          apiClient.get<LectionaryResponse>(`/bulletins/services/${selectedService.service_id}/lectionary`)
            .catch(err => { if (err.response?.status === 404) return { data: null }; throw err; }),
          apiClient.get<ExportPreferences>(`/bulletins/indesign-settings/services/${selectedService.service_id}/export-preferences`),
        ]);
        if (cancelled) return;
        const entry = response.data?.entry || null;
        setLectionaryEntry(entry);
        setCollectCount(response.data?.collectCount ?? 1);
        setPrefaceKeyFromCollect(response.data?.prefaceKey ?? null);
        const prefs = { ...saved.data };
        if (entry?.hasTracks && !prefs.track) prefs.track = response.data?.defaultTrack || '1';
        setSavedPreferences(prefs);
        setPreacher(prefs.preacher); setPreacherSp(prefs.preacher_sp);
        setPreacherId(prefs.preacher_person_id ?? null);
        setCollectIndex(prefs.collect);
        setReadingSelections({
          track: prefs.track || undefined, version: prefs.version || '', options: prefs.options,
          optionalVerses: prefs.optionalVerses === 'drop' ? 'drop' : '',
          vigilLessons: prefs.vigilLessons ? prefs.vigilLessons.split(',').map(Number) : undefined,
          vigilResponses: prefs.vigilResponses === 'on',
        });
      } catch (err: any) {
        if (cancelled) return;
        console.warn('[InDesignExport] Could not load lectionary:', err);
        // Don't show error for 404 - just means no lectionary data available
        if (err.response?.status !== 404) {
          setLectionaryError('Failed to load Sunday export choices. Reload the page to try again.');
        }
        setLectionaryEntry(null);
      } finally {
        if (!cancelled) setLectionaryLoading(false);
      }
    };

    fetchLectionary();
    return () => { cancelled = true; };
  }, [selectedService]);

  // The 12:30 service record for the selected Sunday (the 12:30 column of the plan
  // and the Spanish exports use it).
  const spanishSibling = selectedService
    ? (selectedService.time.slice(0, 5) === '12:30' ? selectedService
      : services.find(s => s.date === selectedService.date && s.time.slice(0, 5) === '12:30') || null)
    : null;

  const preferences: ExportPreferences = {
    ...(savedPreferences?.preacher_managed ? { preacher_managed: true, preacher_person_id: preacherId, preacher_revision: savedPreferences.preacher_revision } : {}),
    preacher, preacher_sp: preacherSp, track: readingSelections.track || savedPreferences?.track || '',
    options: readingSelections.options, collect: collectIndex,
    version: readingSelections.version || '',
    optionalVerses: readingSelections.optionalVerses === 'drop' ? 'drop' : '',
    vigilLessons: readingSelections.vigilLessons ? readingSelections.vigilLessons.join(',') : '',
    vigilResponses: readingSelections.vigilResponses ? 'on' : '',
  };
  const preferencesDirty = !!savedPreferences && (
    (savedPreferences.preacher_managed && preacherId !== savedPreferences.preacher_person_id) ||
    preferences.preacher !== savedPreferences.preacher || preferences.preacher_sp !== savedPreferences.preacher_sp ||
    preferences.track !== savedPreferences.track || preferences.collect !== savedPreferences.collect ||
    (preferences.version || '') !== (savedPreferences.version || '') ||
    (preferences.optionalVerses || '') !== (savedPreferences.optionalVerses || '') ||
    (preferences.vigilLessons || '') !== (savedPreferences.vigilLessons || '') ||
    (preferences.vigilResponses || '') !== (savedPreferences.vigilResponses || '') ||
    (['ot', 'psalm', 'nt', 'gospel'] as const).some(slot => preferences.options[slot] !== savedPreferences.options[slot])
  );
  const savePreferences = async () => {
    if (!selectedService) return;
    setExporting('saving'); setError(null); setNotice('');
    try {
      const response = await apiClient.put<ExportPreferences>(
        `/bulletins/indesign-settings/services/${selectedService.service_id}/export-preferences`, preferences);
      setSavedPreferences(response.data);
      setPreacher(response.data.preacher); setPreacherSp(response.data.preacher_sp);
      setPreacherId(response.data.preacher_person_id ?? null);
      setNotice('Preacher and reading choices saved for all three bulletins this Sunday.');
    } catch (err: any) { setError(typeof err.response?.data?.error === 'string' ? err.response.data.error : 'Could not save Sunday export choices. Please try again.'); }
    finally { setExporting(null); }
  };
  // One of the three on its own, from the same saved choices as "Export all three".
  const handleExportOne = async (key: BulletinKey) => {
    if (!selectedService) return;
    setExporting(key); setError(null); setNotice('');
    try {
      const [file] = await createIndesignExports(selectedService.date, services, [key]);
      downloadExport(file.blob, file.filename);
      setNotice(`Download started for ${file.filename}.`);
    } catch (err: any) {
      const detail = err.response?.data?.error;
      setError(typeof detail === 'string' ? detail : detail?.message || err.message || 'Could not export. Please try again.');
    }
    finally { setExporting(null); }
  };
  const handleExportAll = async () => {
    if (!selectedService) return;
    setExporting('all'); setError(null); setNotice('');
    try {
      const files = await createIndesignExports(selectedService.date, services);
      files.forEach(file => downloadExport(file.blob, file.filename));
      setNotice('Download started for all three files. Use each JSON with its InDesign script.');
    } catch (err: any) {
      const detail = err.response?.data?.error;
      setError(typeof detail === 'string' ? detail : detail?.message || err.message || 'Could not export all three. Please try again.');
    }
    finally { setExporting(null); }
  };

  // Export JSON for the selected service
  const handleExport = async (type: ServiceType) => {
    if (!selectedService || (usesPlan(type) && !eightAmReady)) return;

    setError(null);
    setExporting(type);
    try {
      let endpoint: string;
      let filename: string;

      // Format date for filename
      const dateStr = selectedService.date.replace(/-/g, '');
      const serviceName = (selectedService.name || 'bulletin')
        .replace(/\s*-\s*(Sunday|Saturday|Monday|Tuesday|Wednesday|Thursday|Friday).*$/i, '')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '_');

      // Build query params for reading selections
      const params = new URLSearchParams();

      // Add track selection if applicable
      if (lectionaryEntry?.hasTracks && readingSelections.track) {
        params.set('track', readingSelections.track);
      }

      // Palm Sunday version (only sent when not the day's default)
      if (lectionaryEntry?.versions && readingSelections.version) {
        params.set('readingsVersion', readingSelections.version);
      }

      // Optional verses, Easter Vigil lessons
      if (readingSelections.optionalVerses === 'drop') params.set('optionalVerses', 'drop');
      if (lectionaryEntry?.vigilLessons && readingSelections.vigilLessons) {
        params.set('vigilLessons', readingSelections.vigilLessons.join(','));
      }
      if (lectionaryEntry?.vigilLessons && readingSelections.vigilResponses) params.set('vigilResponses', 'on');

      // Add option selections for each slot
      if (readingSelections.options.ot !== undefined) {
        params.set('otOption', String(readingSelections.options.ot));
      }
      if (readingSelections.options.psalm !== undefined) {
        params.set('psalmOption', String(readingSelections.options.psalm));
      }
      if (readingSelections.options.nt !== undefined) {
        params.set('ntOption', String(readingSelections.options.nt));
      }
      if (readingSelections.options.gospel !== undefined) {
        params.set('gospelOption', String(readingSelections.options.gospel));
      }

      // Multi-collect picker (only meaningful when collectCount > 1)
      if (collectCount > 1 && collectIndex > 0) {
        params.set('collect', String(collectIndex));
      }

      // Eucharistic Prayer and Prayers of the People
      if (eucharist) {
        params.set('eucharist', eucharist);
        params.set('form', eucharistForm);
      }
      // Baptism / Renewal replace the Prayers of the People slot — only one of the three.
      if (pop && !baptismForm && !renewal) {
        params.set('pop', pop);
      }
      if (baptismForm) {
        params.set('baptism', baptismForm);
        const packed = encodeBaptismPeople(baptismPeople);
        if (packed) params.set('baptismNames', packed);
      } else if (renewal) {
        params.set('renewal', 'on');
      }
      if (postcommunion) {
        params.set('postcommunion', postcommunion);
      }
      // Proper Preface (only sent when EP is selected — preface is meaningless without one)
      if (eucharist && preface) {
        params.set('preface', preface);
      }

      if (usesPlan(type)) {
        params.set('variant', type === '8am' ? '8am' : type === '10am' ? '10am' : '1230');
        // Explicit empty values override seasonal defaults, including intentional omissions.
        Object.entries(currentChoices).forEach(([key, value]) => params.set(key, value ?? ''));
      }
      // The 10 am is laid out fully each week (update-bulletin-10am.jsx, since
      // 2026-09-22). Prayers left empty here come from the seasonal plan.
      if (type === '10am') params.set('variant', '10am');
      const queryString = params.toString();
      const baseQuery = queryString ? `?${queryString}` : '';

      switch (type) {
        case '8am':
          endpoint = `/bulletins/services/${selectedService.service_id}/export/indesign${baseQuery}`;
          filename = `${dateStr}_${serviceName}_8am.json`;
          break;
        case '10am':
          endpoint = `/bulletins/services/${selectedService.service_id}/export/indesign${baseQuery}`;
          filename = `${dateStr}_${serviceName}_10am.json`;
          break;
        case 'spanish':
        case 'spanishFull': {
          // Spanish hymn selections live on the 12:30 PM service for the date.
          // If the user picked a non-12:30 card (e.g. the 10am), switch to the
          // 12:30 sibling for the same date so we hit the right service_id.
          let spanishServiceId = selectedService.service_id;
          if (selectedService.time !== '12:30') {
            const sibling = services.find(
              (s) => s.date === selectedService.date && s.time === '12:30'
            );
            if (!sibling) {
              setError(`No 12:30 PM Spanish service found for ${selectedService.date}.`);
              setExporting(null);
              return;
            }
            spanishServiceId = sibling.service_id;
          }
          endpoint = `/bulletins/services/${spanishServiceId}/export/indesign${baseQuery ? baseQuery + '&' : '?'}lang=sp`;
          filename = `${dateStr}_${serviceName}_sp${type === 'spanish' ? '_mp' : ''}.json`;
          break;
        }
        case 'bilingual':
          endpoint = `/bulletins/services/${selectedService.service_id}/export/indesign-bilingual${baseQuery}`;
          filename = `${dateStr}_${serviceName}_bilingual.json`;
          break;
      }

      const response = await apiClient.get(endpoint, {
        responseType: 'json'
      });

      // Add preacher names to the JSON data
      const jsonData = response.data;
      if (!savedPreferences?.preacher_managed && preacher.trim()) {
        jsonData.preacher = preacher.trim();
      }
      if (!savedPreferences?.preacher_managed && preacherSp.trim()) {
        jsonData.preacher_sp = preacherSp.trim();
      }

      if (type === '8am') {
        await apiClient.post(`/bulletins/indesign-settings/services/${selectedService.service_id}/exports`, {
          filename, payload: jsonData,
        });
      }

      // Create download link - using a more robust approach for cross-browser compatibility
      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);

      // Use setTimeout to ensure the element is in the DOM before clicking
      setTimeout(() => {
        a.click();
        // Delay cleanup to ensure download starts
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
      }, 0);
    } catch (err: any) {
      console.error('Export failed:', err);
      const detail = err.response?.data?.error;
      setError(typeof detail === 'string' ? detail : detail?.message || 'Export failed. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Clean liturgy name for display
  const cleanLiturgyName = (name: string) => {
    return (name || '')
      .replace(/\s*-\s*(Sunday|Saturday|Monday|Tuesday|Wednesday|Thursday|Friday).*$/i, '')
      .trim();
  };

  if (loading) {
    return (
      <div className="indesign-export-settings">
        <div className="loading">Loading upcoming services...</div>
      </div>
    );
  }

  return (
    <div className="indesign-export-settings">
      <section className="all-bulletins-panel">
        <label htmlFor="bulletin-sunday">Sunday</label>
        <select id="bulletin-sunday" value={selectedService?.service_id || ''} disabled={exporting !== null}
          onChange={e => {
            setEightAmReady(false); setLectionaryLoading(true); setSavedPreferences(null);
            setSelectedService(services.find(s => s.service_id === Number(e.target.value)) || null);
          }}>
          <option value="" disabled>Choose a Sunday</option>
          {services.filter(s => s.church_id === 2).map(service =>
            <option key={service.service_id} value={service.service_id}>
              {formatDate(service.date)} — {cleanLiturgyName(service.name)}
              {service.time.slice(0, 5) !== '10:00' ? ` (${service.time})` : ''}
            </option>)}
        </select>
        <p>8 am · 10 am · 12:30 Spanish — download all three JSON files.</p>
        {savedPreferences && <p>
          {savedPreferences.track ? `Track ${savedPreferences.track} where applicable` : 'Track follows the planned readings'}
          {' · '}{savedPreferences.preacher}
        </p>}
        <p>Uses the seasonal plan's prayers for all three services. All psalm verses and brackets are included.</p>
        <button type="button" className="export-button primary" onClick={handleExportAll}
          disabled={!selectedService || selectedService.time.slice(0, 5) !== '10:00' || !savedPreferences || lectionaryLoading || exporting !== null || preferencesDirty || eightAmPending || (variant === '8am' && !eightAmReady)}>
          {exporting === 'all' ? 'Exporting all three…' : 'Export all three'}
        </button>
        <div className="export-one-buttons">
          <span>Or just one:</span>
          {([['8am', '8 am'], ['10am', '10 am'], ['sp', '12:30']] as [BulletinKey, string][]).map(([key, label]) =>
            <button key={key} type="button" className="export-button" onClick={() => handleExportOne(key)}
              disabled={!selectedService || selectedService.time.slice(0, 5) !== '10:00' || !savedPreferences || lectionaryLoading || exporting !== null || preferencesDirty || eightAmPending}>
              {exporting === key ? 'Exporting…' : label}
            </button>)}
        </div>
        {selectedService && selectedService.time.slice(0, 5) !== '10:00' && <p>Use an individual export below for this service time.</p>}
        {(preferencesDirty || eightAmPending) && <p role="status">Save or cancel your changes below before exporting all three.</p>}
        {notice && <p role="status">{notice}</p>}
      </section>
      {error && <div className="error-message" role="alert">{error}</div>}
      {lectionaryError && <div className="error-message" role="alert">{lectionaryError}</div>}

      {selectedService && (
        <details className="export-panel">
          <summary>Customize this Sunday</summary>
          <fieldset disabled={exporting !== null}>
          <h3>Export: {cleanLiturgyName(selectedService.name)}</h3>
          <p className="export-date">
            {new Date(selectedService.date + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
          </p>

          <div className="preacher-input">
            <label htmlFor="bulletin-variant">Bulletin:</label>
            <select id="bulletin-variant" value={variant} disabled={exporting !== null}
              onChange={e => { setEightAmReady(false); setVariant(e.target.value as ServiceType); }}>
              <option value="8am">8:00 AM — spoken</option>
              <option value="10am">10:00 AM — full music</option>
              <option value="spanishFull">12:30 PM — Spanish</option>
              <option value="spanish">12:30 PM — Spanish Morning Prayer</option>
              <option value="bilingual">Bilingual</option>
            </select>
          </div>
          {variant === '8am' && <EightAmTemplatePanel key={selectedService.service_id}
            service={selectedService} choices={currentChoices} onApply={applyChoices} onReady={setEightAmReady} onPendingChanges={setEightAmPending} />}
          {variant === '10am' && <EightAmTemplatePanel key={`10am-${selectedService.service_id}`} column="10am"
            service={selectedService} choices={currentChoices} onApply={applyChoices} onReady={setEightAmReady} onPendingChanges={setEightAmPending} />}
          {variant === 'spanishFull' && (spanishSibling
            ? <EightAmTemplatePanel key={`1230-${spanishSibling.service_id}`} column="1230"
                service={spanishSibling} choices={currentChoices} onApply={applyChoices} onReady={setEightAmReady} onPendingChanges={setEightAmPending} />
            : <p className="error-message">No 12:30 PM Spanish service found for {selectedService.date}.</p>)}
          {variant === 'spanish' && <p>Only for Spanish Morning Prayer Sundays, with <code>update-bulletin-mp-spanish.jsx</code>. For the Eucharist choose “12:30 PM — Spanish”.</p>}

          {/* Lectionary Readings Panel - shows track/options when available */}
          {lectionaryEntry ? (
            <ReadingsConfigPanel
              entry={lectionaryEntry}
              selections={readingSelections}
              onSelectionsChange={setReadingSelections}
              loading={lectionaryLoading}
              error={lectionaryError}
            />
          ) : (
            // Fallback to simple readings display when no lectionary data
            <div className="readings-preview">
              <h4>Readings</h4>
              {lectionaryLoading ? (
                <div className="readings-config-loading">Loading lectionary data...</div>
              ) : (
                <>
                  <div className="reading-item">
                    <span className="reading-label">First Reading:</span>
                    <span className="reading-reference">{selectedService.ot_reading || 'N/A'}</span>
                  </div>
                  <div className="reading-item">
                    <span className="reading-label">Psalm:</span>
                    <span className="reading-reference">{selectedService.psalm_reading || 'N/A'}</span>
                  </div>
                  <div className="reading-item">
                    <span className="reading-label">Second Reading:</span>
                    <span className="reading-reference">{selectedService.nt_reading || 'N/A'}</span>
                  </div>
                  <div className="reading-item">
                    <span className="reading-label">Gospel:</span>
                    <span className="reading-reference">{selectedService.gospel_reading || 'N/A'}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {savedPreferences?.preacher_managed ? <div className="preacher-input">
            <label htmlFor="scheduled-preacher">Preacher for all services:</label>
            <select id="scheduled-preacher" value={preacherId ?? ''} disabled={exporting !== null}
              onChange={e => { const id = e.target.value ? Number(e.target.value) : null; const person = preachers.find(p => p.id === id);
                setPreacherId(id); setPreacher(person?.bulletin_name_en || 'The Rev. Alfredo Feregrino'); setPreacherSp(person?.bulletin_name_es || 'El Rvdo. Alfredo Feregrino'); }}>
              <option value="">Default: The Rev. Alfredo Feregrino</option>{preachers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <p>{preacher}{preacherSp && <> · {preacherSp}</>}</p>
            <p>Uses the shared preaching schedule, defaulting to Alfredo when no preacher is selected. Save below to update it. <Link to="/staff">Manage schedule and preachers</Link></p>
          </div> : <>
            <div className="preacher-input"><label htmlFor="preacher">Preacher:</label><input id="preacher" value={preacher} onChange={e => setPreacher(e.target.value)} /></div>
            <div className="preacher-input"><label htmlFor="preacher-sp">Predicador (Spanish):</label><input id="preacher-sp" value={preacherSp} onChange={e => setPreacherSp(e.target.value)} /></div>
          </>}

          {collectCount > 1 && (
            <div className="preacher-input">
              <label htmlFor="collect-pick">Collect:</label>
              <select
                id="collect-pick"
                value={collectIndex}
                onChange={(e) => setCollectIndex(parseInt(e.target.value, 10))}
              >
                {Array.from({ length: collectCount }, (_, i) => (
                  <option key={i} value={i}>
                    Collect {i + 1}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="preacher-input">
            <label htmlFor="eucharist-pick">Eucharistic Prayer:</label>
            <select
              id="eucharist-pick"
              value={eucharist}
              onChange={(e) => setEucharist(e.target.value as EucharistChoice)}
            >
              <option value="">— None (omit from export) —</option>
              <option value="A">Prayer A</option>
              <option value="B">Prayer B</option>
              <option value="C">Prayer C</option>
              <option value="D">Prayer D</option>
              <option value="EOW2">EOW Prayer 2</option>
            </select>
            {eucharist && eucharist !== 'EOW2' && (
              <select
                style={{ marginLeft: '0.5rem' }}
                value={eucharistForm}
                onChange={(e) => setEucharistForm(e.target.value as 'short' | 'long')}
              >
                <option value="short">Short form</option>
                <option value="long">Long (full text)</option>
              </select>
            )}
            {eucharist === 'EOW2' && (
              <span
                style={{ marginLeft: '0.5rem', fontSize: '0.85em', fontStyle: 'italic', color: '#666' }}
                title="EOW2 is always printed in full — it isn't in the pew BCP."
              >
                (full text — no short form)
              </span>
            )}
          </div>

          <div className="preacher-input">
            <label htmlFor="pop-pick">Prayers of the People:</label>
            <select
              id="pop-pick"
              value={pop}
              onChange={(e) => setPop(e.target.value as PopChoice)}
              disabled={!!baptismForm || renewal}
            >
              <option value="">— None (omit from export) —</option>
              <option value="form-i">Form I</option>
              <option value="form-i-short">Form I (St. Luke's Short)</option>
              <option value="form-ii">Form II</option>
              <option value="form-iii">Form III</option>
              <option value="form-iv">Form IV</option>
              <option value="form-v">Form V</option>
              <option value="form-vi">Form VI</option>
            </select>
            {(baptismForm || renewal) && (
              <small style={{ display: 'block', color: '#666', marginTop: 4 }}>
                {baptismForm ? 'Baptism' : 'Renewal of Vows'} replaces the Prayers of the People.
              </small>
            )}
          </div>

          <div className="preacher-input">
            <label htmlFor="baptism-pick">Holy Baptism:</label>
            <select
              id="baptism-pick"
              value={baptismForm}
              onChange={(e) => setBaptismForm(e.target.value as '' | 'older' | 'younger' | 'group')}
              disabled={renewal}
            >
              <option value="">— None —</option>
              <option value="older">Adults / Older Children (answers for self)</option>
              <option value="younger">Infants / Younger Children (sponsors answer)</option>
              <option value="group">Several candidates (names listed once; the book's N.)</option>
            </select>
            {baptismForm && (
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'block' }}>
                  Who is being baptized:
                </label>
                {baptismPeople.map((person, i) => (
                  <div
                    key={i}
                    style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}
                  >
                    <input
                      type="text"
                      aria-label={`Name of person ${i + 1} being baptized`}
                      value={person.name}
                      placeholder={i === 0 ? 'e.g. Yanis' : 'Name'}
                      onChange={(e) =>
                        setBaptismPeople((prev) =>
                          prev.map((p, j) => (j === i ? { ...p, name: e.target.value } : p))
                        )
                      }
                    />
                    <select
                      aria-label={`Boy or girl, person ${i + 1}`}
                      value={person.gender}
                      onChange={(e) =>
                        setBaptismPeople((prev) =>
                          prev.map((p, j) =>
                            j === i ? { ...p, gender: e.target.value as 'm' | 'f' } : p
                          )
                        )
                      }
                    >
                      <option value="f">Girl</option>
                      <option value="m">Boy</option>
                    </select>
                    <button
                      type="button"
                      aria-label={`Remove person ${i + 1}`}
                      title="Remove"
                      disabled={baptismPeople.length === 1}
                      onClick={() =>
                        setBaptismPeople((prev) => prev.filter((_, j) => j !== i))
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setBaptismPeople((prev) => [...prev, { name: '', gender: 'f' }])
                  }
                >
                  + Add another
                </button>
                <small style={{ display: 'block', color: '#666', marginTop: 6 }}>
                  {baptismForm === 'group'
                    ? 'The names print once as a list under the heading. Both presentations print as the prayer book lays them out, and the baptism and the signing print once with N. in place of each name, so boy / girl does not matter here.'
                    : baptismPeople.filter((p) => p.name.trim()).length > 1
                    ? 'Two or more: the rite uses the prayer books’ plural wording, and the presentation, the baptism itself and the signing with the cross repeat once per person.'
                    : 'Boy / girl sets the Spanish word endings (bautizado / bautizada).'}
                </small>
              </div>
            )}
          </div>

          <div className="preacher-input">
            <label htmlFor="renewal-toggle">Renewal of Baptismal Vows:</label>
            <select
              id="renewal-toggle"
              value={renewal ? 'on' : ''}
              onChange={(e) => setRenewal(e.target.value === 'on')}
              disabled={!!baptismForm}
            >
              <option value="">— None —</option>
              <option value="on">Include (Easter Vigil / Pentecost / baptismal feasts)</option>
            </select>
            {renewal && (
              <small style={{ display: 'block', color: '#666', marginTop: 4 }}>
                Replaces the Nicene Creed and the Prayers of the People.
              </small>
            )}
          </div>

          <div className="preacher-input">
            <label htmlFor="postcommunion-pick">Post-Communion Prayer:</label>
            <select
              id="postcommunion-pick"
              value={postcommunion}
              onChange={(e) => setPostcommunion(e.target.value as PostcommunionChoice)}
            >
              <option value="">— None (omit from export) —</option>
              <option value="eow1">EOW Prayer 1 (God of abundance)</option>
              <option value="eow2">EOW Prayer 2 (Loving God)</option>
              <option value="bcp-almighty">BCP Rite II (Almighty and everliving God)</option>
            </select>
          </div>

          {prefaceOptions.length > 0 && (
            <div className="preacher-input">
              <label htmlFor="preface-pick">
                Proper Preface:
                {prefaceKeyFromCollect && (
                  <span style={{ marginLeft: 8, fontWeight: 'normal', fontSize: '0.85em', color: '#666' }}>
                    (suggested: {prefaceKeyFromCollect})
                  </span>
                )}
              </label>
              <select
                id="preface-pick"
                value={preface}
                onChange={(e) => setPreface(e.target.value)}
                disabled={!eucharist || eucharist === 'D' || eucharist === 'EOW2'}
                title={
                  !eucharist
                    ? 'Pick a Eucharistic Prayer first'
                    : eucharist === 'D'
                    ? "Prayer D always uses 'Of God the Father'"
                    : eucharist === 'EOW2'
                    ? 'EOW Prayer 2 has its own narrative — no Proper Preface'
                    : ''
                }
              >
                {usesPlan(variant) && <option value="auto">Follow the day</option>}
                <option value="">— None (leave EP placeholder text) —</option>
                {prefaceOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
              {preface && (() => {
                const sel = prefaceOptions.find((o) => o.id === preface);
                return sel ? (
                  <div style={{ marginTop: 6, padding: '6px 10px', background: '#f7f7f7', borderRadius: 4, fontSize: '0.85em', fontStyle: 'italic', color: '#444' }}>
                    {sel.text}
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {variant === 'spanishFull' && (
            <div className="preacher-input">
              <label htmlFor="fraction-pick">Fraction anthem (spoken):</label>
              <select id="fraction-pick" value={fraction} onChange={(e) => setFraction(e.target.value)}>
                <option value="">— None (heading only) —</option>
                {fractionOptions.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
              </select>
            </div>
          )}

          {usesPlan(variant) && (variant !== 'spanishFull' || spanishSibling) && (
            <AddedPartsPanel churchId={variant === 'spanishFull' ? spanishSibling!.church_id : selectedService.church_id}
              value={parts} onChange={setParts} language={variant === 'spanishFull' ? 'es' : 'en'} />
          )}

          <div className="template-actions">
            <button type="button" onClick={savePreferences}
              disabled={!savedPreferences || !preferencesDirty || (!savedPreferences.preacher_managed && (!preacher.trim() || !preacherSp.trim())) || exporting !== null}>
              Save preacher and readings for this Sunday
            </button>
            <button type="button" disabled={!savedPreferences || !preferencesDirty || exporting !== null}
              onClick={() => { if (savedPreferences) {
                setPreacher(savedPreferences.preacher); setPreacherSp(savedPreferences.preacher_sp); setPreacherId(savedPreferences.preacher_person_id ?? null);
                setReadingSelections({ track: savedPreferences.track || undefined, options: savedPreferences.options });
                setCollectIndex(savedPreferences.collect);
              } }}>Reset preacher and readings</button>
          </div>
          <p>Saved preacher and reading choices apply to all three bulletins. Use “Save this Sunday only” above to keep one service’s prayers and added parts for this Sunday.</p>
          <div className="export-buttons">
            <button className="export-button primary" onClick={() => handleExport(variant)}
              disabled={exporting !== null || lectionaryLoading || (savedPreferences?.preacher_managed && preferencesDirty) || (usesPlan(variant) && !eightAmReady)}>
              {exporting ? 'Exporting…' : `Export ${variant === '8am' ? '8:00 AM' : variant === '10am' ? '10:00 AM' : variant === 'spanish' ? '12:30 PM Spanish Morning Prayer' : variant === 'spanishFull' ? '12:30 PM Spanish' : 'Bilingual'}`}
            </button>
          </div>

          <div className="export-instructions">
            <h4>Usage Instructions</h4>
            <ol>
              <li>Export the JSON file for your service type</li>
              <li>Open your InDesign bulletin template</li>
              <li>Run the appropriate script from Window &gt; Utilities &gt; Scripts</li>
              <li>Select the downloaded JSON file when prompted</li>
            </ol>
            <p className="scripts-note">
              Scripts available: <code>update-bulletin-8am.jsx</code>, <code>update-bulletin-10am.jsx</code>,
              <code>update-bulletin-spanish.jsx</code>, <code>update-bulletin-bilingual.jsx</code>,
              and for Morning Prayer the <code>update-bulletin-mp-…</code> scripts
            </p>
          </div>
          </fieldset>
        </details>
      )}
    </div>
  );
};

export default InDesignExportSettings;
